import React, { useEffect, useState } from "react";
import { Button, Card, Col, DatePicker, Input, Menu, message, Modal, Row, Select, Table, Tag } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import urls from "urls";
import { hvoFirestore, vceFirestore } from "configs/FirebaseConfig";
import { FIRESTORE_CAMPAIGNS_TABLE } from "constants/FirestoreConstant";
import Loading from "components/shared-components/Loading";
import utils from "utils";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import { useCampaigns } from "data/hooks/useCampaigns";

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const CampaignsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [shopSourceFilter, setShopSourceFilter] = useState("all");
  const { campaigns, isLoading } = useCampaigns({ shopSource: shopSourceFilter });
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [dateRange, setDateRange] = useState([]);

  useEffect(() => {
    if (campaigns) {
      setFilteredCampaigns(campaigns);
    }
  }, [campaigns]);

  useEffect(() => {
    filterCampaigns();
  }, [campaigns, searchText, statusFilter, platformFilter, dateRange]);

  const filterCampaigns = () => {
    if (!campaigns) return;

    let filtered = campaigns;

    if (searchText) {
      filtered = utils.wildCardSearch(filtered, searchText);
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((campaign) => campaign.status === statusFilter);
    }

    if (platformFilter !== "all") {
      filtered = filtered.filter((campaign) => campaign.platform === platformFilter);
    }

    if (dateRange.length === 2) {
      filtered = filtered.filter((campaign) => {
        if (!campaign.startDate) return false;

        let startDate;
        if (campaign.startDate.toDate && typeof campaign.startDate.toDate === "function") {
          startDate = dayjs(campaign.startDate.toDate());
        } else if (campaign.startDate instanceof Date) {
          startDate = dayjs(campaign.startDate);
        } else {
          startDate = dayjs(campaign.startDate);
        }

        return startDate.isBetween(dateRange[0], dateRange[1], null, "[]");
      });
    }

    setFilteredCampaigns(filtered);
  };

  const handleDelete = async (campaign) => {
    try {
      const firestore = campaign.shopSource === "VCE" ? vceFirestore : hvoFirestore;
      await firestore.collection(FIRESTORE_CAMPAIGNS_TABLE).doc(campaign.id).delete();
      message.success(t("campaign_deleted"));
    } catch (error) {
      console.error("Error deleting campaign:", error);
      message.error(t("error_deleting_campaign"));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "default",
      active: "success",
      paused: "warning",
      completed: "error",
    };
    return colors[status] || "default";
  };

  const getPlatformColor = (platform) => {
    const colors = {
      meta_ads: "blue",
      google_ads: "green",
      email_marketing: "orange",
      other: "default",
    };
    return colors[platform] || "default";
  };

  const getShopSourceColor = (shopSource) => {
    return shopSource === "VCE" ? "cyan" : "magenta";
  };

  const dropdownMenu = (record) => (
    <Menu>
      <Menu.Item key="1" onClick={() => navigate(`${urls.editCampaign}/${record.id}?shopSource=${record.shopSource}`)}>
        <EditOutlined />
        <span style={{ marginLeft: 5 }}>{t("edit")}</span>
      </Menu.Item>
      <Menu.Item
        key="2"
        onClick={() => {
          Modal.confirm({
            title: t("confirm_delete"),
            onOk: () => handleDelete(record),
            okText: t("yes"),
            cancelText: t("no"),
          });
        }}
      >
        <DeleteOutlined />
        <span style={{ marginLeft: 5 }}>{t("delete")}</span>
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: t("shop"),
      dataIndex: "shopSource",
      key: "shopSource",
      render: (shopSource) => <Tag color={getShopSourceColor(shopSource)}>{shopSource}</Tag>,
      width: 80,
    },
    {
      title: t("campaign_name"),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => utils.antdTableSorter(a, b, "name"),
      render: (name, record) => (
        <a
          onClick={() => navigate(`${urls.campaignDetail}/${record.id}?shopSource=${record.shopSource}`)}
          style={{ cursor: "pointer", fontWeight: 500 }}
        >
          {name}
        </a>
      ),
    },
    {
      title: t("platform"),
      dataIndex: "platform",
      key: "platform",
      render: (platform) => <Tag color={getPlatformColor(platform)}>{t(platform)}</Tag>,
    },
    {
      title: t("status"),
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={getStatusColor(status)}>{t(status)}</Tag>,
    },
    {
      title: t("start_date"),
      dataIndex: "startDate",
      key: "startDate",
      render: (date) => {
        if (!date) return "-";
        if (date.toDate && typeof date.toDate === "function") {
          return dayjs(date.toDate()).format("DD-MM-YYYY");
        } else if (date instanceof Date) {
          return dayjs(date).format("DD-MM-YYYY");
        } else if (typeof date === "string" || typeof date === "number") {
          return dayjs(date).format("DD-MM-YYYY");
        }
        return "-";
      },
      sorter: (a, b) => {
        if (!a.startDate && !b.startDate) return 0;
        if (!a.startDate) return -1;
        if (!b.startDate) return 1;

        const getTimestamp = (date) => {
          if (!date) return 0;
          if (date.toDate && typeof date.toDate === "function") {
            return dayjs(date.toDate()).unix();
          } else if (date instanceof Date) {
            return dayjs(date).unix();
          } else {
            return dayjs(date).unix();
          }
        };

        return getTimestamp(a.startDate) - getTimestamp(b.startDate);
      },
    },
    {
      title: t("end_date"),
      dataIndex: "endDate",
      key: "endDate",
      render: (date, record) => {
        if (record.isOngoing) return <Tag color="blue">{t("ongoing")}</Tag>;
        if (!date) return "-";

        if (date.toDate && typeof date.toDate === "function") {
          return dayjs(date.toDate()).format("DD-MM-YYYY");
        } else if (date instanceof Date) {
          return dayjs(date).format("DD-MM-YYYY");
        } else if (typeof date === "string" || typeof date === "number") {
          return dayjs(date).format("DD-MM-YYYY");
        }
        return "-";
      },
    },
    {
      title: t("budget"),
      dataIndex: "budget",
      key: "budget",
      render: (budget) => (budget ? `€${budget.toLocaleString()}` : "-"),
      sorter: (a, b) => (a.budget || 0) - (b.budget || 0),
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_, record) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(record)} />
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading cover="content" />;
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <h2>{t("campaigns")}</h2>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(urls.addCampaign)}>
            {t("add_campaign")}
          </Button>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder={t("search_campaigns")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder={t("shop_source")}
              value={shopSourceFilter}
              onChange={setShopSourceFilter}
              style={{ width: "100%" }}
            >
              <Option value="all">{t("all_shops")}</Option>
              <Option value="VCE">VCE</Option>
              <Option value="HVO">HVO</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select placeholder={t("status")} value={statusFilter} onChange={setStatusFilter} style={{ width: "100%" }}>
              <Option value="all">{t("all_statusses")}</Option>
              <Option value="draft">{t("draft")}</Option>
              <Option value="active">{t("active")}</Option>
              <Option value="paused">{t("paused")}</Option>
              <Option value="completed">{t("completed")}</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder={t("platform")}
              value={platformFilter}
              onChange={setPlatformFilter}
              style={{ width: "100%" }}
            >
              <Option value="all">{t("all_platforms")}</Option>
              <Option value="meta_ads">{t("meta_ads")}</Option>
              <Option value="google_ads">{t("google_ads")}</Option>
              <Option value="email_marketing">{t("email_marketing")}</Option>
              <Option value="other">{t("other")}</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              style={{ width: "100%" }}
              value={dateRange}
              onChange={(dates) => setDateRange(dates || [])}
              format="DD-MM-YYYY"
            />
          </Col>
        </Row>

        <Table
          showSorterTooltip={false}
          columns={columns}
          dataSource={filteredCampaigns}
          rowKey={(record) => `${record.shopSource}-${record.id}`}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} ${t("of")} ${total} ${t("campaigns")}`,
          }}
        />
      </Card>
    </div>
  );
};

export default CampaignsList;
